import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useUnreadCount, useLearnerMessages } from '../hooks/useMessages'
import { useMessageSSE } from '../hooks/useMessageSSE'
import { formatDistanceToNow } from 'date-fns'
import { getDateLocale } from '@/utils/dateLocale'
import type { NewMessageEvent } from '../types/message'

interface NotificationBadgeProps {
  className?: string
}

export function NotificationBadge({ className = '' }: NotificationBadgeProps) {
  const { t } = useTranslation('messages')
  const [isOpen, setIsOpen] = useState(false)
  const { data: unreadData } = useUnreadCount()
  const { data: messagesData } = useLearnerMessages()

  const handleNewMessage = useCallback((event: NewMessageEvent) => {
    // 브라우저 알림 표시 (권한 있는 경우)
    if (Notification.permission === 'granted') {
      new Notification(t('notification.newMessage'), {
        body: event.title,
        icon: '/favicon.ico'
      })
    }
  }, [])

  // SSE 연결
  useMessageSSE({
    enabled: true,
    onNewMessage: handleNewMessage
  })

  const unreadCount = unreadData?.count ?? 0
  const messages = messagesData?.messages ?? []

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200">
            <div className="p-3 border-b border-gray-200">
              <h3 className="font-medium">{t('notification.title')}</h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {t('notification.empty')}
                </div>
              ) : (
                messages.slice(0, 5).map((msg) => (
                  <Link
                    key={msg.id}
                    to={`/messages/${msg.id}`}
                    onClick={() => setIsOpen(false)}
                    className={`block p-3 hover:bg-gray-50 border-b border-gray-100 ${
                      !msg.isRead ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex gap-2">
                      <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                        msg.isRead ? 'bg-gray-300' : 'bg-blue-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{msg.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatDistanceToNow(new Date(msg.createdAt), {
                            addSuffix: true,
                            locale: getDateLocale()
                          })}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
            <div className="p-2 border-t border-gray-200">
              <Link
                to="/messages"
                onClick={() => setIsOpen(false)}
                className="block text-center text-sm text-blue-600 hover:underline"
              >
                {t('notification.viewAll')}
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
